import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "../../shared/seo/Seo";
import { urls, copy, currentLang } from "../../shared/seo/links";
import BlogMainSection from "../../components/blogs/BlogsMainSection";
import LawyersPagination from "../../components/lawyers/LawyersPagination";
import Heading from "../../components/blogs/Heading";
import BlogPostCard from "../../components/blogs/BlogsPostCard";
import PageContent from "../../components/blogs/PageContent";
import SideBar from "../../components/blogs/SideBar";
import ComponentsLoader from "../../components/includes/ComponentsLoader";
import TopicLinks from "../../components/content/TopicLinks";
import NotFound from "../NotFound/NotFound";
import LoadError from "../../shared/components/LoadError";
import { LOAD_FAILED, LOAD_NOT_FOUND, loadOutcome, loadWithRetry } from "../../shared/helpers/loadResult";
import { useBlogContext } from "../../shared/context/BlogContext";

/**
 * /Magazine-Juridique?page=N: the same page of articles as the server
 * renders for that address (POST /blog/listing), with page links and a self
 * canonical, so every page of the listing is its own indexable page.
 */
export default function Blogs() {
  const lang = currentLang();
  const [searchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);
  const [blogs, setBlogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(true);
  // A failed request (rate limited, server or network down) is not "no
  // article": it offers a retry. Only a 404 is the not-found page.
  const [outcome, setOutcome] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [topics, setTopics] = useState([]);
  // The topic links sit above the articles: they show together with the
  // articles, so arriving first or later does not push the list down.
  const [topicsLoaded, setTopicsLoaded] = useState(false);
  const { getBlogsListing, getBlogCategories } = useBlogContext();

  useEffect(() => {
    let cancelled = false;
    async function fetchBlogs() {
      setIsLoading(true);
      setOutcome(null);
      const response = await loadWithRetry(() => getBlogsListing({ lang, page }), () => !cancelled);
      if (cancelled) return;
      const result = loadOutcome(response);
      setOutcome(result);
      if (result !== LOAD_FAILED) {
        setBlogs(response.data?.blogs ?? []);
        setTotal(response.data?.total ?? 0);
        setPageSize(response.data?.pageSize ?? 20);
      }
      setIsLoading(false);
    }
    fetchBlogs();
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
    // getBlogsListing is left out: the provider recreates it on every render.
  }, [lang, page, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  // Topic pages with content in this language.
  useEffect(() => {
    let live = true;
    getBlogCategories({ language: lang }).then((res) => {
      if (!live) return;
      setTopics(res.data?.categories ?? []);
      setTopicsLoaded(true);
    });
    return () => {
      live = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const failed = outcome === LOAD_FAILED;
  const loaded = !isLoading && !failed;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  // A page past the last one is a 404 on the server too.
  if (outcome === LOAD_NOT_FOUND || (loaded && page > pages)) return <NotFound />;

  const text = copy.blogs(lang, { page });
  const pathFor = (p) => urls.withPage(urls.sectionPath("blogs", lang), p);

  return (
    <>
      <Heading />
      <PageContent>
        <Seo
          title={text.title}
          description={text.description}
          path={pathFor(page)}
          alternates={urls.alternates((l) => urls.withPage(urls.sectionPath("blogs", l), page))}
        />
        <BlogMainSection>
          {failed ? (
            <div className="col-12">
              <LoadError onRetry={() => setAttempt((n) => n + 1)} />
            </div>
          ) : isLoading || !topicsLoaded ? (
            <ComponentsLoader page />
          ) : (
            <>
              <div className="col-12">
                <TopicLinks topics={topics} lang={lang} />
              </div>
              {blogs.map((blog) => (
                <BlogPostCard key={blog._id || blog.id} blog={blog} />
              ))}
              <div className="col-12">
                <LawyersPagination page={page} pages={pages} pathFor={pathFor} />
              </div>
            </>
          )}
        </BlogMainSection>
        <SideBar />
      </PageContent>
    </>
  );
}
